import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  DollarSign, Calendar, Users, CheckCircle, Clock, Plus,
  RefreshCw, FileText, Printer, ChevronDown, AlertCircle, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

export default function PayrollPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token'), manager = null } = context;
  const [loading, setLoading] = useState(true);
  const [payrolls, setPayrolls] = useState([]);
  const [preview, setPreview] = useState(null);
  
  // Create Payroll Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    period_start: '',
    period_end: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  
  // View Payroll Modal
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payrollDetails, setPayrollDetails] = useState(null);

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchPayrolls = useLatestCallback(async () => {
    setLoading(true);
    try {
      const params = manager?.restaurant_id ? `?restaurant_id=${manager.restaurant_id}` : '';
      const response = await axios.get(`${API}/payroll/list${params}`, getHeaders());
      setPayrolls(response.data.payrolls || []);
    } catch (error) {
      toast.error('Failed to load payrolls');
      console.error(error);
    } finally {
      setLoading(false);
    }
  });

  const fetchPreview = useLatestCallback(async () => {
    if (!createForm.period_start || !createForm.period_end) return;
    
    try {
      // the restaurant is the session's (labor-service ignores a restaurant_id)
      const params = new URLSearchParams({
        period_start: createForm.period_start,
        period_end: createForm.period_end
      });
      const response = await axios.get(`${API}/payroll/preview?${params}`, getHeaders());
      setPreview(response.data);
    } catch (error) {
      console.error('Preview error:', error);
    }
  });

  const createPayroll = async () => {
    if (!createForm.period_start || !createForm.period_end) {
      toast.error('Please select a date range');
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/payroll/create`, {
        period_start: createForm.period_start,
        period_end: createForm.period_end
      }, getHeaders());
      
      toast.success(response.data.message);
      setShowCreateModal(false);
      setPreview(null);
      setCreateForm({ period_start: '', period_end: '' });
      fetchPayrolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payroll');
    } finally {
      setIsCreating(false);
    }
  };

  const viewPayrollDetails = async (payrollId) => {
    try {
      const response = await axios.get(`${API}/payroll/${payrollId}`, getHeaders());
      setPayrollDetails(response.data);
      setSelectedPayroll(payrollId);
    } catch (error) {
      toast.error('Failed to load payroll details');
    }
  };

  const approvePayroll = async (payrollId) => {
    try {
      await axios.post(`${API}/payroll/${payrollId}/approve`, {}, getHeaders());
      toast.success('Payroll approved');
      fetchPayrolls();
      if (payrollDetails) viewPayrollDetails(payrollId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const markPaid = async (payrollId) => {
    try {
      await axios.post(`${API}/payroll/${payrollId}/mark-paid`, {}, getHeaders());
      toast.success('Payroll marked as paid');
      fetchPayrolls();
      if (payrollDetails) viewPayrollDetails(payrollId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const deletePayroll = async (payrollId) => {
    if (!confirm('Are you sure you want to delete this payroll?')) return;
    
    try {
      await axios.delete(`${API}/payroll/${payrollId}`, getHeaders());
      toast.success('Payroll deleted');
      fetchPayrolls();
      setSelectedPayroll(null);
      setPayrollDetails(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  useEffect(() => {
    if (token) {
      fetchPayrolls();
    }
  }, [token, fetchPayrolls]);

  useEffect(() => {
    if (createForm.period_start && createForm.period_end) {
      fetchPreview();
    }
  }, [createForm.period_start, createForm.period_end, fetchPreview]);

  // Set default dates for create form
  const setDefaultDates = () => {
    const today = new Date();
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - today.getDay());
    const prevSunday = new Date(lastSunday);
    prevSunday.setDate(lastSunday.getDate() - 7);
    
    setCreateForm({
      period_start: prevSunday.toISOString().split('T')[0],
      period_end: lastSunday.toISOString().split('T')[0]
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline" className="bg-gray-100">Draft</Badge>;
      case 'approved':
        return <Badge className="bg-blue-100 text-blue-700">Approved</Badge>;
      case 'paid':
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading && payrolls.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Payroll</h1>
          <p className="text-gray-500">Manage employee pay based on timesheet hours</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchPayrolls}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { setDefaultDates(); setShowCreateModal(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Payroll
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Payrolls</p>
                <p className="text-2xl font-bold">{payrolls.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold">
                  {payrolls.filter(p => p.status === 'draft' || p.status === 'approved').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Paid</p>
                <p className="text-2xl font-bold">
                  {payrolls.filter(p => p.status === 'paid').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Paid Out</p>
                <p className="text-2xl font-bold">
                  ${payrolls.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.total_amount || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payrolls List */}
      <Card>
        <CardHeader>
          <CardTitle>Payroll History</CardTitle>
        </CardHeader>
        <CardContent>
          {payrolls.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>No payrolls yet. Create your first payroll to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrolls.map((payroll) => (
                    <TableRow key={payroll.id}>
                      <TableCell>
                        <div className="font-medium">
                          {payroll.period_start} - {payroll.period_end}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(payroll.status)}</TableCell>
                      <TableCell className="text-right font-mono">
                        {payroll.total_hours?.toFixed(1)}h
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        ${payroll.total_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-sm text-gray-500">
                        {new Date(payroll.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => viewPayrollDetails(payroll.id)}
                          >
                            View
                          </Button>
                          {payroll.status === 'draft' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-blue-600"
                                onClick={() => approvePayroll(payroll.id)}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600"
                                onClick={() => deletePayroll(payroll.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {payroll.status === 'approved' && (
                            <Button 
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => markPaid(payroll.id)}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Payroll Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Payroll</DialogTitle>
            <DialogDescription>
              Select a pay period to calculate employee wages from timesheet entries
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={createForm.period_start}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, period_start: e.target.value }))}
                />
              </div>
              <div>
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={createForm.period_end}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, period_end: e.target.value }))}
                />
              </div>
            </div>

            {/* Preview */}
            {preview && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold mb-3">Payroll Preview</h4>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-sm text-gray-500">Employees</p>
                    <p className="text-xl font-bold">{preview.summary?.total_employees || 0}</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-sm text-gray-500">Total Hours</p>
                    <p className="text-xl font-bold">{preview.summary?.total_hours?.toFixed(1) || 0}h</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <p className="text-sm text-gray-500">Total Pay</p>
                    <p className="text-xl font-bold text-green-600">
                      ${preview.summary?.total_gross_pay?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>

                {preview.employees?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead className="text-right">Reg Hrs</TableHead>
                          <TableHead className="text-right">OT Hrs</TableHead>
                          <TableHead className="text-right">Gross Pay</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.employees.map((emp) => (
                          <TableRow key={emp.staff_id}>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell className="text-right">{emp.regular_hours?.toFixed(1)}</TableCell>
                            <TableCell className="text-right">{emp.overtime_hours?.toFixed(1)}</TableCell>
                            <TableCell className="text-right font-semibold">
                              ${emp.gross_pay?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {preview.employees?.length === 0 && (
                  <p className="text-center text-gray-500 py-4">
                    No unpaid time entries found for this period
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createPayroll}
              disabled={isCreating || !preview?.employees?.length}
            >
              {isCreating ? 'Creating...' : 'Create Payroll'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Payroll Details Modal */}
      <Dialog open={!!selectedPayroll} onOpenChange={() => { setSelectedPayroll(null); setPayrollDetails(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payroll Details</DialogTitle>
            <DialogDescription>
              {payrollDetails?.payroll?.period_start} - {payrollDetails?.payroll?.period_end}
            </DialogDescription>
          </DialogHeader>
          
          {payrollDetails && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(payrollDetails.payroll?.status)}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${payrollDetails.payroll?.total_amount?.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Entries */}
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Regular</TableHead>
                      <TableHead className="text-right">Overtime</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Bonus</TableHead>
                      <TableHead className="text-right">Gross Pay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollDetails.entries?.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-medium">{entry.staff_name}</TableCell>
                        <TableCell className="text-right">{entry.regular_hours?.toFixed(1)}h</TableCell>
                        <TableCell className="text-right">{entry.overtime_hours?.toFixed(1)}h</TableCell>
                        <TableCell className="text-right">${entry.hourly_rate?.toFixed(2)}/hr</TableCell>
                        <TableCell className="text-right">${entry.bonus?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell className="text-right font-semibold">
                          ${entry.gross_pay?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                {payrollDetails.payroll?.status === 'draft' && (
                  <>
                    <Button 
                      variant="outline" 
                      className="text-red-600"
                      onClick={() => deletePayroll(selectedPayroll)}
                    >
                      Delete
                    </Button>
                    <Button onClick={() => approvePayroll(selectedPayroll)}>
                      Approve
                    </Button>
                  </>
                )}
                {payrollDetails.payroll?.status === 'approved' && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => markPaid(selectedPayroll)}
                  >
                    Mark as Paid
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
